/**
 * Daily Intro Gate — decides whether today's first login shows the
 * full-screen check-in moment before any app UI renders.
 *
 * Rules (see dailyIntro.logic.shouldShowIntro):
 *  - server truth: /wellness/status hasCheckedInToday
 *  - local mark: once done/skipped today, never re-gates (per user, per day)
 *  - fails OPEN: an API error or crash inside the intro never locks the app
 */
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import apiClient from '../../services/api/config';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../common/LoadingScreen';
import DailyIntroExperience from './DailyIntroExperience';
import {
  IntroMark,
  localDateISO,
  readIntroMark,
  shouldShowIntro,
  writeIntroMark,
} from './dailyIntro.logic';

interface DailyIntroGateProps {
  children: ReactNode;
}

// TEST MODE — accounts listed here replay the intro on EVERY full page
// load (F5 to watch it again), and never persist a done/skipped mark.
// Empty in normal operation: every account, including the ones used for
// demos, then gets the intro once a day like everyone else.
const FORCE_INTRO_EMAILS: string[] = [];
// Module-level so SPA route changes don't replay it mid-session; a full
// page reload resets it.
let forcedIntroShownThisLoad = false;

export default function DailyIntroGate({ children }: DailyIntroGateProps) {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const todayISO = localDateISO();
  const userId = user?.id;

  const [mark, setMark] = useState<IntroMark>(() => (userId ? readIntroMark(userId, todayISO) : null));
  const [intro, setIntro] = useState<'idle' | 'active' | 'finished'>('idle');
  const [appMounted, setAppMounted] = useState(false);
  const markedDoneFromServer = useRef(false);

  // Re-read the mark when the signed-in user (or the day) changes.
  useEffect(() => {
    setMark(userId ? readIntroMark(userId, todayISO) : null);
  }, [userId, todayISO]);

  const preblocked =
    !user ||
    user.role === 'super-super-admin' ||
    !!user.mustChangePassword ||
    location.pathname === '/questionnaire' ||
    mark !== null;

  const statusQuery = useQuery({
    queryKey: ['daily-intro-status', userId, todayISO],
    queryFn: async () => {
      const r = await apiClient.get('/wellness/status');
      return r.data?.data ?? null;
    },
    enabled: isAuthenticated && !!user && !preblocked && intro === 'idle',
    staleTime: Infinity,
    retry: 1,
  });

  const hasCheckedInToday: boolean | null = statusQuery.isSuccess
    ? Boolean(statusQuery.data?.hasCheckedInToday)
    : null;

  // Server says today is already handled — remember locally so navigation
  // (and tomorrow's stale cache) never re-asks.
  useEffect(() => {
    if (userId && statusQuery.isSuccess && statusQuery.data?.hasCheckedInToday && !markedDoneFromServer.current) {
      markedDoneFromServer.current = true;
      writeIntroMark(userId, todayISO, 'done');
      setMark('done');
    }
  }, [userId, todayISO, statusQuery.isSuccess, statusQuery.data]);

  const forced =
    !!user &&
    user.role !== 'super-super-admin' &&
    !user.mustChangePassword &&
    location.pathname !== '/questionnaire' &&
    FORCE_INTRO_EMAILS.includes((user.email || '').toLowerCase());

  const show =
    (forced && !forcedIntroShownThisLoad) ||
    (!preblocked &&
      shouldShowIntro({
        role: user?.role,
        mustChangePassword: user?.mustChangePassword,
        pathname: location.pathname,
        mark,
        hasCheckedInToday,
      }));

  // Once the intro starts it owns its lifecycle — writing the mark mid-flow
  // (after submit) must not unmount it before the exit animation.
  useEffect(() => {
    if (intro === 'idle' && show) {
      if (forced) forcedIntroShownThisLoad = true;
      setIntro('active');
    }
  }, [intro, show, forced]);

  if (intro === 'active' && user) {
    const finish = () => {
      setAppMounted(true);
      setIntro('finished');
    };
    return (
      <>
        {appMounted && children}
        <ErrorBoundary fallbackRender={() => null} onError={finish}>
          <DailyIntroExperience
            firstName={user.firstName || ''}
            userId={userId}
            streak={Number(statusQuery.data?.streak) || 0}
            onMountApp={() => setAppMounted(true)}
            onFinished={() => setIntro('finished')}
            onComplete={(m) => {
              // Test accounts never persist a mark — the intro must replay
              // on the next full page load.
              if (forced) return;
              if (userId) writeIntroMark(userId, todayISO, m);
              setMark(m);
            }}
          />
        </ErrorBoundary>
      </>
    );
  }

  // Deciding: hold the quiet glass loader instead of flashing the dashboard.
  if (intro === 'idle' && !preblocked && statusQuery.isPending) {
    return <LoadingScreen quiet />;
  }

  return <>{children}</>;
}
